/** Reconnect schedule from UX spec 7.1: 1, 2, 4, 8, 15, 30 s with ±20% jitter. */
export const BACKOFF_STEPS_MS = [1000, 2000, 4000, 8000, 15000, 30000] as const;

/**
 * The phone's SIP WebSocket reconnect policy, handed to the call engine (browserEngine.ts): the same
 * steps and jitter, `offline` after 5 failed tries with the last failure shown, then a try every 30 s
 * that never stops on its own. "Retry now", coming back online and waking from sleep try at once.
 */
export const PHONE_RECONNECT_POLICY = Object.freeze({ reconnectDelaysMs: BACKOFF_STEPS_MS, reconnectJitter: 0.2, offlineAfterAttempts: 5 });

export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const index = Math.min(Math.max(attempt, 0), BACKOFF_STEPS_MS.length - 1);
  const base = BACKOFF_STEPS_MS[index] ?? 30000;
  const jitter = 0.8 + 0.4 * Math.min(Math.max(random(), 0), 1);
  return Math.round(base * jitter);
}

/** Command status polling (UX spec 7.4): 1, 2, 4 s, then every 10 s. */
export function commandPollDelay(attempt: number): number {
  return attempt <= 0 ? 1000 : attempt === 1 ? 2000 : attempt === 2 ? 4000 : 10000;
}

/** A new idempotency key for one user intent. Reuse it only to reconcile that same intent. */
export function newIdempotencyKey(prefix = 'web'): string {
  const id =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : Array.from(globalThis.crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');
  return `${prefix}:${id}`;
}
